import loadjs from 'loadjs';
import { jsonp } from './init';
import { relative } from './utils/relate';
import { appInfo, qs } from '@weapp/utils';

export interface Obj {
  [key: string]: any
}

const externals: Obj = {
  'react': 'React',
  'react-dom': 'ReactDOM',
  'react-router-dom': 'ReactRouterDOM',
  'axios': 'axios',
  'mobx': 'mobx',
  'mobx-react': 'mobxReact',
  'mobx-react-lite': 'mobxReactLite',
  'loadjs': 'loadjs',
}

const fixExtension = (path: string) => {
  return (!path || /\.js$/.test(path) || /\.vue$/.test(path)) ? path : `${path}.js`;
}
/**
 * 根据路径查找不同类型文件信息
 * 1. 带后缀，直接返回
 * 2. 不带后缀，主动匹配.js,.vue文件
 * @param appInfo
 * @param filePath
 */
const matchFileInfo = (appInfo: any, filePath: string) => {
  let fileInfo = null;
  if(appInfo){
    if(!filePath || /\.js$/.test(filePath) || /\.vue$/.test(filePath)){
      fileInfo = appInfo?.[filePath]
    }else {
      const matchArr = [
        `${filePath}.js`,
        `${filePath}.vue`,
        `${filePath}/index.js`,
        `${filePath}/index.vue`
      ]
      let len = matchArr.length;
      for (let i = 0; i < len; i++) {
        let currentPath = matchArr[i];
        if(appInfo?.[currentPath]){
          filePath = currentPath;
          fileInfo = appInfo[currentPath];
          break;
        }
      }
    }
  }

  return {
    matchPath:filePath,
    fileInfo
  }
}

jsonp.export = (appId: string, filePath: string, moduleExports: () => any) => {
  if (appId && /^[^.]/.test(filePath) && typeof moduleExports === 'function') {
    if (!jsonp[appId]) {
      jsonp[appId] = {};
    }
    jsonp[appId][filePath] = moduleExports;
    return;
  }
  console.error(`Ecode export module failed: appId "${appId}", filePath "${filePath}"`)
};

jsonp.require = (appId: string, filePath: string, relativePath?: string) => {
  if (appId && filePath && /^[^.]/.test(filePath)) {
    // filePath = fixExtension(filePath);
    if (jsonp[appId] && (!relativePath || /^\./.test(relativePath))) {
      let requirePath = relative(filePath, relativePath || '') || filePath;
      let mod:any = null;
      const {matchPath,fileInfo} = matchFileInfo(jsonp[appId],requirePath);
      if(fileInfo) {
        mod = fileInfo;
        requirePath = matchPath;
      }else {
        mod = jsonp[appId]?.[requirePath];
      }
      // 循环引用返回 undefined 处理
      if (mod && mod.fetching) {
        console.error(`Recycling applications are not supported.
Cannot access [appId "${appId}", filePath "${filePath}", relativePath "${relativePath}"] before initialization.`);
        return {};
      }
      if (mod && mod.exports) {
        return mod.exports;
      }
      if (typeof mod === 'function') {
        jsonp[appId][requirePath].fetching = true;
        const modExports = mod(jsonp.require);
        jsonp[appId][requirePath].exports = modExports;
        delete jsonp[appId][requirePath].fetching;
        return modExports;
      }
    } else if (relativePath){
      if (/^@weapp\//.test(relativePath)) {
        const mod = window[appInfo(relativePath).libraryName];
        if (mod) {
          return mod;
        }
      } else if (externals[relativePath]) {
        const mod = window[externals[relativePath]];
        if (mod) {
          return mod;
        }
      } else {
        let cusPlugin = window.weappEcodesdk.pluginManager.getPluginInstance(relativePath);
        if(cusPlugin){
          return cusPlugin;
        }
      }
    }
  }
  console.error(`Ecode require module failed: appId "${appId}", filePath "${filePath}", relativePath "${relativePath}"`)
}

export interface TempObj {
  key: string;
  resolve: (reason?: any) => void;
  reject: (reason?: any) => void;
}
export interface Temp {
  [key: string]: TempObj[];
}

export interface LoadedCache {
  [key: string]: boolean;
}

const temp: Temp = {};

const loadedCache: LoadedCache = {};
const cssLoadedCache: LoadedCache = {};

// 第一次请求成功前，重复请求挂起
function dispath(appId: string, action: 'resolve' | 'reject', err?: any) {
  if (temp[appId].length) {
    while(temp[appId].length) {
      let result = err;
      if (action === 'resolve') {
        const filePath = temp[appId][0].key;
        let {matchPath,fileInfo} = matchFileInfo(jsonp?.[appId], filePath);
        if (fileInfo) {
          result = jsonp.require(appId, matchPath)
        } else {
          action = 'reject';
          console.error(result);
        }
      }
      temp[appId][0][action](result);
      if (temp[appId].length) {
        action = 'resolve';
      }
      temp[appId].shift();
    }
  }
  loadedCache[appId] = true;
}

/**
 * 默认按以下优先级获取版本号
 * - ls缓存ecode_load_version > url路径参数 ecode_load_version
 * @returns 代码块加载的版本号，获取异常或不存在以当前时间戳兜底
 */
function getEcodeLoadVersion() {
  try {
    const lsVal = localStorage.getItem('ecode_load_version');
    if (lsVal) {
      return lsVal;
    }
    let searchParams = window.location.search;
    if (searchParams.startsWith('?')) searchParams = searchParams.substring(1);
    const params = qs.parse(searchParams);
    if (params['ecode_load_version']) {
      return params['ecode_load_version']
    }
  } catch (err) { }
  return new Date().getTime();
}

function checkHasType(fileTypes: string[], checkType: string) {
  if (Array.isArray(fileTypes) && fileTypes.length > 0) {
    const tmps = fileTypes.map(t => t.toLowerCase());
    if (tmps.includes(checkType)) return true;
    else return false;
  } else {
    return true;
  }
}

/**
 * 是否仅加载css
 * @param fileTypes 加载文件类型
 * @returns 
 */
function isOnlyCss (fileTypes?: string[]) {
  return !checkHasType(fileTypes || [], 'js') && checkHasType(fileTypes || [], 'css');
}

/**
 * 是否仅加载Js
 * @param fileTypes 加载文件类型
 * @returns 
 */
function isOnlyJs (fileTypes?: string[]) {
  return checkHasType(fileTypes || [], 'js') && !checkHasType(fileTypes || [], 'css');
}

/**
 * 单独处理代码块的加载逻辑
 * @param errorMsg 
 * @param publicUrl 
 * @param publicDomain 
 * @param chunksPrefix 
 * @param appId 
 * @param filePath 
 * @param fileTypes 
 * @param resolve 
 * @param reject 
 * @returns 
 */
function asyncImportBySimple(errorMsg: string, publicUrl: string, publicDomain: string, chunksPrefix:string, 
    appId: string, filePath: string, fileTypes: string[], resolve: any, reject: any) {
  // 仅css加载进行单独判断
  if (isOnlyCss(fileTypes) && cssLoadedCache[appId]) {
    resolve({});
    return;
  }
  if (!isOnlyCss(fileTypes)) {
    // 成功加载过 chunk 并且 chunk 中有 filePath 模块，直接返回
    let {matchPath,fileInfo} = matchFileInfo(jsonp?.[appId], filePath);
    if (fileInfo) {
      resolve(jsonp.require(appId, matchPath));
      return;
    }

    // 成功加载过 chunk 且其他 filePath 模块存在，则直接抛错当前 filePath 模块
    if (loadedCache[appId]) {
      reject(errorMsg);
      return;
    }

    if (temp[appId]) {
      temp[appId].push({ key: filePath, resolve, reject });
      return;
    }
    temp[appId] = [{ key: filePath, resolve, reject }];
  }

  const loadKey = `__ecode_chunks_${appId}__` + isOnlyCss(fileTypes);
  let originChunks = [];
  // 如果请求地址中含参数ecode_load_version 或者 ls 中含有则 以此为版本号
  let loadversion = getEcodeLoadVersion();
  if (Array.isArray(fileTypes) && fileTypes.length > 0) {
    if (checkHasType(fileTypes, 'js')) {
      originChunks.push(`${chunksPrefix}simple/${appId}/index.js?v=${loadversion}`);  
    }
    if (checkHasType(fileTypes, 'css')) {
      originChunks.push(`${chunksPrefix}simple/${appId}/index.css?v=${loadversion}`);            
    }
  } else {
    originChunks.push(`${chunksPrefix}simple/${appId}/index.js?v=${loadversion}`);
    originChunks.push(`${chunksPrefix}simple/${appId}/index.css?v=${loadversion}`);
  }
  if (originChunks.length === 0) {
    originChunks.push(`${chunksPrefix}simple/${appId}/index.js?v=${loadversion}`);
    originChunks.push(`${chunksPrefix}simple/${appId}/index.css?v=${loadversion}`);
  }
  jsonp[appId] = {
    chunks: originChunks.map(chunk => `${publicDomain}${publicUrl}${chunk}`)
  }
  loadjs(jsonp[appId].chunks, loadKey);
  loadjs.ready(loadKey, {
    success: () => {
      if (isOnlyCss(fileTypes)) { // 有且只有css
        cssLoadedCache[appId] = true;
        resolve({});
        return;
      }
      if (isOnlyJs(fileTypes)) {
        dispath(appId, 'resolve', errorMsg);
      } else {
        cssLoadedCache[appId] = true;
        dispath(appId, 'resolve', errorMsg);
      }
    },
    error: (err: any) => {
      // console.error(errorLoad, err);
      dispath(appId, 'reject', `load simple app failed: appId "${appId}", "${jsonp[appId].chunks}"`);
    }
  });
  return;
}

/**
 * 校验异常是否单纯css加载失败
 * @param err 
 * @returns 
 */
function checkErrorIsOnlyCss(err: any) {
  try {
    if (!Array.isArray(err) || err.length !== 1) return false;
    if (err[0].indexOf('/index.css?') > -1) return true;
    if ((err[0] || '').indexOf('/index_v_') > -1 && (err[0] || '').endsWith('.css')) return true;
  } catch (e) {
    return false;
  }
}

// 针对代码块提供 fileTypes 控制，满足仅加载css的情况，防止空js异常报错
export const asyncImport = (appId: string, filePath: string, isSimpleMode?: boolean, fileTypes?: string[]) => new Promise((resolve, reject) => {
  const error = `Ecode async import() failed: appId "${appId}", filePath "${filePath}"`;
  if (appId && /^[^.]/.test(filePath)) {
    // filePath = fixExtension(filePath);
    // 占用 @weapp/ecode 的二级路径变量规则
    const { publicUrl } = appInfo('@weapp/ecode');
    const publicDomain = window['publicDomainfileEcode'] || '';
    const chunksPrefix = window.localStorage.getItem('__ecode_chunks_prefix__') || ''; 
    if (isSimpleMode && chunksPrefix) { // 简易模式 不需要对比缓存，直接加载请求
      asyncImportBySimple(error, publicUrl, publicDomain, chunksPrefix, appId, filePath, fileTypes || [], resolve, reject);
      return;
    }
    // 成功加载过 chunk 并且 chunk 中有 filePath 模块，直接返回
    let {matchPath,fileInfo} = matchFileInfo(jsonp?.[appId], filePath);
     if (fileInfo) {
      resolve(jsonp.require(appId, matchPath));
      return;
    }

    // 成功加载过 chunk 且其他 filePath 模块存在，则直接抛错当前 filePath 模块
    if (loadedCache[appId]) {
      reject(error);
      return;
    }
    // 没有加载过的模块但是有 chunks, 发起加载
    if (jsonp?.[appId]?.chunks) {
      const errorLoad = `load chunk failed: appId "${appId}", "${jsonp[appId].chunks}"`;
      const loadOptions = {
        success: () => {
          dispath(appId, 'resolve', error);
        },
        error: (err: any) => {
          if (checkErrorIsOnlyCss(err)) {
            dispath(appId, 'resolve', error);
          } else {
            console.error(errorLoad, err);
            dispath(appId, 'reject', errorLoad);
          }
        }
      };

      // 正在执行后续代码
      if (temp[appId]) {
        temp[appId].push({
          key: filePath,
          resolve, reject
        });
        return;
      }

      temp[appId] = [{
        key: filePath,
        resolve, reject
      }];

      const loadKey = `__ecode_chunks_${appId}__`;
      let chunks = jsonp[appId].chunks;
      if (Array.isArray(chunks) && chunks.length) {
        chunks = chunks.map(chunk => `${publicDomain}${publicUrl}${chunk}`);
      }
      loadjs(chunks, loadKey, loadOptions);
      return;
    }
  }
  console.error(error);
  reject(error);
});

/**
 * 异步加载组件，支持插件模式，插件加载完成后再引入组件
 * @param appId
 * @param filePath
 * @param plugins
 * @param isSimpleMode
 * @param fileTypes
 */
export const asyncImportPlugin =async (appId: string, filePath: string, plugins?: string | string[], isSimpleMode?: boolean, fileTypes?: string[] )=>{
  await window.weappEcodesdk.pluginManager.loadPlugin(plugins||'');
  return asyncImport(appId,filePath,isSimpleMode, fileTypes);
}

export { jsonp, relative };